import { useEffect, useRef } from 'react';
import { Animated, View } from 'react-native';
import styled from 'styled-components/native';
import { COLORS } from '../../../styles/colors';

// Grey token: COLORS.mediumGray (#C4D0E6) — the lightest grey token in the palette
// that reads clearly as a loading block on the dark card background.
// borderColor (#E1E9EE from SkeletonPlaceholder) is even lighter and less visible
// against dark surfaces, so mediumGray is preferred here.
const SKELETON_COLOR = COLORS.mediumGray; // #C4D0E6
const SKELETON_COLOR_DARK = 'rgba(196, 208, 230, 0.35)'; // dimmed variant for chat bubbles

// ---------- Animated block ----------

const PulseBlock = ({ width, height, borderRadius = 4, style }) => {
  const opacity = useRef(new Animated.Value(0.5)).current;

  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: false,
        }),
        Animated.timing(opacity, {
          toValue: 0.5,
          duration: 800,
          useNativeDriver: false,
        }),
      ])
    );
    anim.start();
    return () => anim.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={[
        {
          backgroundColor: SKELETON_COLOR,
          borderRadius,
          width,
          height,
          opacity,
        },
        style,
      ]}
    />
  );
};

// ---------- Styled layout primitives ----------

const RootView = styled.View``;

const CardRow = styled.View`
  flex-direction: row;
  gap: 10px;
  margin-bottom: 10px;
`;

const CardTextCol = styled.View`
  flex: 1;
  gap: 8px;
  justify-content: center;
`;

const ChatRow = styled.View`
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
`;

// ---------- Variant templates ----------

const CardItem = () => (
  <CardRow>
    {/* Photo area: ~84x56px */}
    <PulseBlock width={84} height={56} borderRadius={6} />
    {/* Text lines */}
    <CardTextCol>
      <PulseBlock width="80%" height={12} />
      <PulseBlock width="60%" height={10} />
      <PulseBlock width="70%" height={10} />
    </CardTextCol>
  </CardRow>
);

const ChatItem = () => (
  <ChatRow>
    <PulseBlock
      width="70%"
      height={36}
      borderRadius={8}
      style={{ backgroundColor: SKELETON_COLOR_DARK }}
    />
    <PulseBlock
      width="55%"
      height={28}
      borderRadius={8}
      style={{ backgroundColor: SKELETON_COLOR_DARK, alignSelf: 'flex-end' }}
    />
  </ChatRow>
);

const TextItem = () => (
  <PulseBlock width="100%" height={12} style={{ marginBottom: 8 }} />
);

const VARIANT_DEFAULTS = {
  card: 1,
  chat: 3,
  text: 1,
};

// ---------- Skeleton ----------

/**
 * Skeleton — grey-block loading placeholder.
 *
 * Props:
 *   variant   'card' | 'chat' | 'text'
 *   count     number of repeated blocks (defaults: card=1, chat=3, text=1)
 *   ariaLabel optional accessible label override
 *
 * Accessibility:
 *   - accessibilityRole="progressbar" + accessibilityState={{ busy: true }}
 *     maps to role="progressbar" aria-busy="true" on React Native Web.
 *   - aria-live="polite" via accessible prop (RNW forwards it).
 *   - accessibilityLabel for screen-reader announcement.
 */
const Skeleton = ({ variant = 'card', count, ariaLabel }) => {
  const resolvedCount = count != null ? count : (VARIANT_DEFAULTS[variant] ?? 1);
  const label = ariaLabel || 'Loading content';

  const renderItem = (i) => {
    if (variant === 'card') return <CardItem key={i} />;
    if (variant === 'chat') return <ChatItem key={i} />;
    return <TextItem key={i} />;
  };

  return (
    <RootView
      accessible
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
      accessibilityLabel={label}
      // React Native Web forwards these additional aria-* props to the DOM
      // eslint-disable-next-line react/no-unknown-property
      aria-busy="true"
      // eslint-disable-next-line react/no-unknown-property
      aria-live="polite"
    >
      {Array.from({ length: resolvedCount }, (_, i) => renderItem(i))}
    </RootView>
  );
};

export default Skeleton;
